import React from 'react';
import './Configuration.scss';
import { useApiFetch } from '../../../api/fetcher';
import { InputText } from 'primereact/inputtext';
import { Button } from 'primereact/button';
import { Card } from 'primereact/card';
import { useNavigate } from 'react-router-dom';
import { Toast } from 'primereact/toast';
import { AutoComplete } from 'primereact/autocomplete';
import { ProgressSpinner } from 'primereact/progressspinner';
import { FLEET_DRIVERS_URL, FLEET_CAR_MAKERS, FLEET_CARS_CONFIGURATION_AVAILABLE, FLEET_CONFIGURE_CARS } from '../../../api/urls';
import type { FleetDriverListDriver } from '../../../types/driverTypes';


type CarModel = { id: number; model_name: string };
type CarMaker = { id: number; maker_name: string; car_models: CarModel[] };
type Availability = { is_fully_configured: boolean; number_of_configured_cars: number; number_of_declared_cars: number; template_name: string };

type PendingConfig = {
    makerId: number;
    makerName: string;
    modelId: number;
    modelName: string;
    plate: string;
    drivers: FleetDriverListDriver[];
};

const normalizePlate = (input: string) => (input || '').toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 15);

const Configuration: React.FC = () => {
    const navigate = useNavigate();
    const apiFetch = useApiFetch();
    const toast = React.useRef<Toast>(null);

    const [makers, setMakers] = React.useState<CarMaker[]>([]);
    const [drivers, setDrivers] = React.useState<FleetDriverListDriver[]>([]);
    const [availability, setAvailability] = React.useState<Availability | null>(null);
    const [loading, setLoading] = React.useState(true);
    const [saving, setSaving] = React.useState(false);
    const [error, setError] = React.useState<string | null>(null);

    // form state
    const [selectedMaker, setSelectedMaker] = React.useState<CarMaker | null>(null);
    const [selectedModel, setSelectedModel] = React.useState<CarModel | null>(null);
    const [makerQuery, setMakerQuery] = React.useState<string>('');
    const [modelQuery, setModelQuery] = React.useState<string>('');
    const [plate, setPlate] = React.useState('');
    const [selectedDrivers, setSelectedDrivers] = React.useState<FleetDriverListDriver[]>([]);
    const [pending, setPending] = React.useState<PendingConfig[]>([]);
    const [editingIndices, setEditingIndices] = React.useState<number[]>([]);
    const [formKey, setFormKey] = React.useState(0);

    // autocomplete suggestions
    const [makerSuggestions, setMakerSuggestions] = React.useState<CarMaker[]>([]);
    const [modelSuggestions, setModelSuggestions] = React.useState<CarModel[]>([]);
    const [driverSuggestions, setDriverSuggestions] = React.useState<FleetDriverListDriver[]>([]);
    const makerRef = React.useRef<AutoComplete>(null);
    const modelRef = React.useRef<AutoComplete>(null);
    const driversRef = React.useRef<AutoComplete>(null);

    const remainingSlots = React.useMemo(() => {
        if (!availability) return 0;
        const used = availability.number_of_configured_cars + pending.length;
        return Math.max(availability.number_of_declared_cars - used, 0);
    }, [availability, pending.length]);

    React.useEffect(() => {
        let cancelled = false;
        (async () => {
            setLoading(true);
            setError(null);
            try {
                const [driversRes, makersRes, availRes] = await Promise.all([
                    apiFetch(FLEET_DRIVERS_URL, { credentials: 'include' }),
                    apiFetch(FLEET_CAR_MAKERS, { credentials: 'include' }),
                    apiFetch(FLEET_CARS_CONFIGURATION_AVAILABLE, { credentials: 'include' }),
                ]);
                const [driversJson, makersJson, availJson] = await Promise.all([
                    driversRes.json(),
                    makersRes.json(),
                    availRes.json(),
                ]);
                if (cancelled) return;
                setDrivers((driversJson?.results ?? driversJson) as FleetDriverListDriver[]);
                setMakers(makersJson as CarMaker[]);
                setAvailability(availJson as Availability);
                const isUnavailable = Boolean(availJson?.is_fully_configured) || ((availJson?.number_of_declared_cars ?? 0) - (availJson?.number_of_configured_cars ?? 0) <= 0);
                if (isUnavailable) {
                    sessionStorage.setItem('flashToast', JSON.stringify({
                        severity: 'warn',
                        summary: 'Informacja',
                        detail: 'Konfiguracja niedostępna - wszystkie samochody dla tego konta są już skonfigurowane',
                        life: 4000
                    }));
                    navigate('/fleet/', { replace: true });
                    return;
                }
            } catch (e) {
                if (!cancelled) setError('Nie udało się załadować danych');
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [apiFetch, navigate]);

    const baseAvailableToAdd = React.useMemo(() => {
        if (!availability) return 0;
        return Math.max(availability.number_of_declared_cars - availability.number_of_configured_cars, 0);
    }, [availability]);

    const canAddCurrent = !!selectedMaker && !!selectedModel && typeof (selectedModel as any)?.id === 'number' && plate.trim().length > 0;

    const resetForm = () => {
        setSelectedMaker(null);
        setSelectedModel(null);
        setMakerQuery('');
        setModelQuery('');
        setPlate('');
        setSelectedDrivers([]);
        setFormKey(k => k + 1);
    };

    const onAddVehicle = () => {
        if (!selectedMaker || !selectedModel || !plate.trim()) return;
        if (remainingSlots <= 0) return;
        const chosenDrivers = selectedDrivers;
        const newItem: PendingConfig = {
            makerId: selectedMaker.id,
            makerName: selectedMaker.maker_name,
            modelId: selectedModel.id,
            modelName: selectedModel.model_name,
            plate: normalizePlate(plate),
            drivers: chosenDrivers,
        };
        setPending(prev => ([...prev, newItem]));
        resetForm();
    };

    const onRemovePending = (idx: number) => {
        setPending(prev => prev.filter((_, i) => i !== idx));
    };

    const onEditPending = (idx: number) => {
        setEditingIndices(prev => (prev.includes(idx) ? prev : [...prev, idx]));
    };
    const onCancelEdit = (idx: number) => {
        setEditingIndices(prev => prev.filter(i => i !== idx));
    };
    const onSaveEdit = (idx: number, updated: PendingConfig) => {
        setPending(prev => prev.map((p, i) => (i === idx ? updated : p)));
        setEditingIndices(prev => prev.filter(i => i !== idx));
    };

    const onSave = async () => {
        if (pending.length === 0 || saving) return;
        setSaving(true);
        try {
            try {
                const availRes = await apiFetch(FLEET_CARS_CONFIGURATION_AVAILABLE);
                const availJson = await availRes.json();
                if (availJson?.is_fully_configured) {
                    setSaving(false);
                    sessionStorage.setItem('flashToast', JSON.stringify({
                        severity: 'warn',
                        summary: 'Informacja',
                        detail: 'Konfiguracja niedostępna - wszystkie samochody dla tego konta są już skonfigurowane',
                        life: 4000
                    }));
                    navigate('/fleet/', { replace: true });
                    return;
                }
            } catch {}

            const payload = pending.map(p => ({
                car_model: p.modelId,
                plate: p.plate,
                driver_ids: p.drivers.map(d => d.id),
            }));
            const res = await apiFetch(FLEET_CONFIGURE_CARS, {
                method: 'POST',
                body: JSON.stringify(payload),
            });
            if (!res.ok) throw new Error('Błąd zapisu');
            sessionStorage.setItem('flashToast', JSON.stringify({
                severity: 'success',
                summary: 'Sukces',
                detail: `Dodano ${pending.length} pojazdy. Pakiety są gotowe do użycia`,
                life: 4000
            }));
            navigate('/fleet/', { replace: true });
        } catch (e) {
            toast.current?.show({ severity: 'error', summary: 'Błąd', detail: 'Nie udało się zapisać konfiguracji', life: 4000 });
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="configuration" style={{ position: 'relative' }}>
            {(loading || saving) && (
                <div className="loading-overlay">
                    <ProgressSpinner style={{ width: 64, height: 64 }} strokeWidth="4" />
                </div>
            )}
            <Toast ref={toast} />
            <div className="card-margins d-flex justify-content-between align-items-center panel-margin-top">
                <Card className="d-flex w-100 justify-content-between card-border">
                    <div className="d-flex justify-content-end w-100">
                        <div className="d-flex">
                            <Button icon="pi pi-arrow-left" className="p-button-text" style={{marginRight: '10px'}} onClick={() => navigate('/fleet/')} />
                            <div className="ml-2">
                                <h2 className="m-0">Aktywacja pakietów</h2>
                                <p className="m-0 text-secondary">Dodaj pojazd, aby wykorzystać dostępne pakiety</p>
                            </div>
                        </div>
                    </div>
                </Card>
            </div>
            {!loading && (
            <div className='card-margins w-100 panel-margin-top-with-margin'>
                <Card className='d-flex w-100 justify-content-center card-border main-card'>
                <div className="d-flex justify-content-center ">
                <Card
                    className="surface-card all-card"
                    title={
                    <div className="d-flex justify-content-left align-items-center">
                        <span className="text-lg font-semibold" style={{marginRight: '10px'}}>Dodane pojazdy</span>
                        <div className='d-flex mt-1'>
                            <span className="text-sm header-badge border-round-md border-1 surface-border px-3 py-1 text-600">
                            {availability ? `${pending.length}/${baseAvailableToAdd}` : '—'}
                            </span>
                        </div>
                    </div>
                    }
                >
                    {pending.length > 0 && (
                        <div className="mb-3">
                            {pending.map((p, idx) => (
                                editingIndices.includes(idx) ? (
                                    <div key={`edit-${idx}`} className="border rounded p-3 mb-2 edit-form">
                                        <div className="grid formgrid p-fluid">
                                            <EditVehicleForm
                                                initial={p}
                                                makers={makers}
                                                drivers={drivers}
                                                onCancel={() => onCancelEdit(idx)}
                                                onSave={(updated) => onSaveEdit(idx, updated)}
                                            />
                                        </div>
                                    </div>
                                ) : (
                                    <div
                                        key={`${p.plate}-${idx}`}
                                        className="border rounded p-3 mb-2 vehicle-summary"
                                    >
                                        <div className="row align-items-center">
                                             <div className="col-10">
                                                <div className="row">
                                                    <div className="col-12 d-flex align-items-center mb-1">
                                                        <span className="status-badge me-3 d-none d-md-flex">
                                                            <img alt='car-configure'style={{marginRight: '3px', marginBottom: '2px'}} src={`/fleet/car/configure-car.svg`} />
                                                            Pojazd {idx + 1}
                                                        </span>
                                                        <span className="fw-semibold">
                                                            {p.makerName} {p.modelName} | {p.plate}
                                                        </span>
                                                    </div>
                                                    {/* Second row: Drivers */}
                                                     {p.drivers && p.drivers.length > 0 && (
                                                        <div className="col-12 mt-2">
                                        <DriverChips drivers={p.drivers} />
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                            <div className="col-2 col-md-2 d-flex justify-content-end align-self-center">
                                                <div className="d-flex gap-1 icon-btns">
                                                    <Button
                                                        icon={<img src="/fleet/car/configuration-edit.svg" alt="edit" style={{height: '18px'}} />}
                                                        className="p-button-text"
                                                        onClick={() => onEditPending(idx)}
                                                    />
                                                    <Button
                                                        icon={<img src="/fleet/car/configuration-remove.svg" alt="remove" style={{height: '18px'}} />}
                                                        className="p-button-text p-button-danger"
                                                        onClick={() => onRemovePending(idx)}
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )
                            ))}
                        </div>
                    )}

                    {remainingSlots > 0 && (
                    <div className="surface-section p-4 border-round-lg mb-4 form-section">
                        <div className="flex justify-content-between align-items-center mb-3">
                            <h6 className="m-0 text-600 font-medium">Wprowadź dane pojazdu:</h6>
                        </div>

                    {error ? (
                        <div className="text-danger">{error}</div>
                    ) : (
                        <>
                    <div className="grid formgrid p-fluid">
                            <div className="row">
                            <div className="col-12 col-lg-4">
                        <label className="block text-600 text-sm mb-2">Marka*</label>
                                <AutoComplete
                                    key={`maker-${formKey}`}
                                    ref={makerRef}
                                    value={makerQuery}
                                    suggestions={makerSuggestions}
                                    completeMethod={(e) => {
                                        const q = String(e.query || '').toLowerCase();
                                        setMakerQuery(String(e.query || ''));
                                        const list = !q ? makers : makers.filter(m => m.maker_name.toLowerCase().includes(q));
                                        setMakerSuggestions(list);
                                    }}
                                    field="maker_name"
                                    onChange={(e) => setMakerQuery(String(e.value || ''))}
                                    onSelect={(e) => { const v = e.value as CarMaker; setSelectedMaker(v); setMakerQuery(v.maker_name); setSelectedModel(null); setModelQuery(''); }}
                                    onFocus={() => { setMakerSuggestions(makers); makerRef.current?.show?.(); }}
                            placeholder="Marka"
                                    dropdown
                        />
                        </div>

                            <div className="col-12 col-lg-4">
                        <label className="block text-600 text-sm mb-2">Model*</label>
                                <AutoComplete
                                    key={`model-${formKey}`}
                                    ref={modelRef}
                                    value={modelQuery}
                                    suggestions={modelSuggestions}
                                    completeMethod={(e) => {
                                        const list = selectedMaker?.car_models ?? [];
                                        const q = String(e.query || '').toLowerCase();
                                        setModelQuery(String(e.query || ''));
                                        setModelSuggestions(!q ? list : list.filter(m => m.model_name.toLowerCase().includes(q)));
                                    }}
                                    field="model_name"
                                    onChange={(e) => { const val = String(e.value || ''); setModelQuery(val); if (selectedModel && val !== selectedModel.model_name) setSelectedModel(null); }}
                                    onSelect={(e) => { const v = e.value as CarModel; setSelectedModel(v); setModelQuery(v.model_name); }}
                                    onFocus={() => { const list = selectedMaker?.car_models ?? []; setModelSuggestions(list); modelRef.current?.show?.(); }}
                            placeholder="Model"
                                    disabled={!selectedMaker}
                                    dropdown
                        />
                        </div>

                            <div className="col-12 col-lg-4">
                        <label className="block text-600 text-sm mb-2">
                                    Numer rejestracyjny*
                        </label>
                        <InputText
                                    key={`plate-${formKey}`}
                                    value={plate}
                                    onChange={(e) => {
                                        const raw = String(e.target.value || '');
                                        const sanitized = raw.replace(/[^a-zA-Z0-9]/g, '');
                                        setPlate(sanitized.toUpperCase().slice(0, 15));
                                    }}
                                    maxLength={15}
                            placeholder="Wprowadź numer"
                        />
                        </div>
                            </div>

                            <div className="field col-12 md:col-6 driver-field">
                        <label className="block text-600 text-sm mb-2">
                                    Kierowcy (opcjonalnie)
                        </label>
                                <AutoComplete
                                    key={`drivers-${formKey}`}
                                    ref={driversRef}
                                    multiple
                                    value={selectedDrivers}
                                    suggestions={driverSuggestions}
                                    completeMethod={(e) => {
                                        const q = String(e.query || '').toLowerCase();
                                        let base = !q
                                            ? drivers
                                            : drivers.filter(d =>
                                                (d.phone_number || '').toLowerCase().includes(q)
                                                || `${d.first_name ?? ''} ${d.last_name ?? ''}`.toLowerCase().includes(q)
                                            );
                                        const selectAll = [{ id: -2, phone_number: 'Wybierz wszystkich', first_name: null, last_name: null } as any];
                                        base = [...selectAll, ...base];
                                        setDriverSuggestions(base);
                                    }}
                                    selectedItemTemplate={(item) => (<span>{item.phone_number}</span>)}
                                    itemTemplate={(item) => (<div>{item.phone_number} {item.first_name || item.last_name ? `• ${(item.first_name ?? '')} ${(item.last_name ?? '')}` : ''}</div>)}
                                    field="phone_number"
                                    onChange={(e) => {
                                        let value = Array.isArray(e.value) ? e.value : [];
                                        const hasAll = value.some((v: any) => v?.id === -2);
                                        if (hasAll) {
                                            value = drivers.slice();
                                        }
                                        setSelectedDrivers(value);
                                    }}
                                    onSelect={() => { driversRef.current?.show?.(); }}
                            placeholder="Kierowca"
                                    onFocus={() => { setDriverSuggestions([{ id: -2, phone_number: 'Wybierz wszystkich' } as any, ...drivers]); driversRef.current?.show?.(); }}
                                    dropdown
                        />
                        </div>

                            <div className="d-flex justify-content-end col-12 text-right mt-1">
                        <Button
                            label="Dodaj pojazd"
                                    className="p-button-primary add-car-button"
                                    onClick={onAddVehicle}
                                    disabled={!canAddCurrent || remainingSlots <= 0}
                        />
                        </div>
                    </div>
                        </>
                    )}
                    </div>
                    )}

                    <div className="d-flex justify-content-end gap-2">
                    <Button
                        label="Anuluj"
                        className="p-button-outlined p-button-secondary"
                            onClick={() => navigate('/fleet/')}
                            disabled={saving}
                    />
                        <Button className="save-and-exit" label="Zapisz i zakończ" disabled={pending.length === 0 || saving} onClick={onSave} />
                    </div>
                </Card>
                </div>
                </Card>
            </div>
            )}
        </div>
    );
};

export default Configuration;

function EditVehicleForm(props: {
    initial: PendingConfig;
    makers: CarMaker[];
    drivers: FleetDriverListDriver[];
    onCancel: () => void;
    onSave: (updated: PendingConfig) => void;
}) {
    const { initial, makers, drivers, onCancel, onSave } = props;
    const [maker, setMaker] = React.useState<CarMaker | null>(() => makers.find(m => m.id === initial.makerId) || null);
    const [model, setModel] = React.useState<CarModel | null>(() => maker?.car_models.find(m => m.id === initial.modelId) || null);
    const [makerQ, setMakerQ] = React.useState<string>(maker?.maker_name ?? '');
    const [modelQ, setModelQ] = React.useState<string>(model?.model_name ?? '');
    const [plate, setPlate] = React.useState<string>(initial.plate);
    const [selectedDrivers, setSelectedDrivers] = React.useState<FleetDriverListDriver[]>(initial.drivers);

    const [makerS, setMakerS] = React.useState<CarMaker[]>([]);
    const [modelS, setModelS] = React.useState<CarModel[]>([]);
    const [driverS, setDriverS] = React.useState<FleetDriverListDriver[]>([]);
    const makerRef = React.useRef<AutoComplete>(null);
    const modelRef = React.useRef<AutoComplete>(null);
    const driversRef = React.useRef<AutoComplete>(null);

    const save = () => {
        if (!maker || !model || !plate.trim()) return;
        onSave({
            makerId: maker.id,
            makerName: maker.maker_name,
            modelId: model.id,
            modelName: model.model_name,
            plate: normalizePlate(plate),
            drivers: selectedDrivers,
        });
    };

    return (
        <div className="edit-form">
            <div className="col-12 d-flex align-items-center mb-1">
                <span className="status-badge me-3">
                    <img alt='car-configure'style={{marginRight: '3px', marginBottom: '2px'}} src={`/fleet/car/configure-car.svg`} />
                    Edycja pojazdu
                        </span>
                    </div>
            <div className='row'>
            <div className="col-12 col-lg-4">
                        <label className="block text-600 text-sm mb-2">Marka*</label>
                <AutoComplete
                    key={`edit-maker-${initial.makerId}`}
                    ref={makerRef}
                    value={makerQ}
                    suggestions={makerS}
                    completeMethod={(e) => {
                        const q = String(e.query || '').toLowerCase();
                        setMakerQ(String(e.query || ''));
                        setMakerS(!q ? makers : makers.filter(m => m.maker_name.toLowerCase().includes(q)));
                    }}
                    field="maker_name"
                    onChange={(e) => setMakerQ(String(e.value || ''))}
                    onSelect={(e) => { const v = e.value as CarMaker; setMaker(v); setMakerQ(v.maker_name); setModel(null); setModelQ(''); }}
                    onFocus={() => { setMakerS(makers); makerRef.current?.show?.(); }}
                            placeholder="Marka"
                    dropdown
                        />
                        </div>
            <div className="col-12 col-lg-4">
                        <label className="block text-600 text-sm mb-2">Model*</label>
                <AutoComplete
                    key={`edit-model-${initial.modelId}`}
                    ref={modelRef}
                    value={modelQ}
                    suggestions={modelS}
                    completeMethod={(e) => {
                        const list = maker?.car_models ?? [];
                        const q = String(e.query || '').toLowerCase();
                        setModelQ(String(e.query || ''));
                        setModelS(!q ? list : list.filter(m => m.model_name.toLowerCase().includes(q)));
                    }}
                    field="model_name"
                    onChange={(e) => { const val = String(e.value || ''); setModelQ(val); if (model && val !== model.model_name) setModel(null); }}
                    onSelect={(e) => { const v = e.value as CarModel; setModel(v); setModelQ(v.model_name); }}
                    onFocus={() => { const list = maker?.car_models ?? []; setModelS(list); modelRef.current?.show?.(); }}
                            placeholder="Model"
                    disabled={!maker}
                    dropdown
                        />
                        </div>
            <div className="col-12 col-lg-4">
                <label className="block text-600 text-sm mb-2">Numer rejestracyjny*</label>
                <InputText key={`edit-plate-${initial.plate}`} value={plate} onChange={(e) => {
                    const raw = String(e.target.value || '');
                    const sanitized = raw.replace(/[^a-zA-Z0-9]/g, '');
                    setPlate(sanitized.toUpperCase().slice(0, 15));
                }} maxLength={15} placeholder="Wprowadź numer" />
                        </div>
                    </div>
            <div className="field col-12 md:col-6">
                <label className="block text-600 text-sm mb-2">Kierowcy (opcjonalnie)</label>
                <AutoComplete
                    key={`edit-drivers-${initial.plate}`}
                    ref={driversRef}
                    multiple
                    value={selectedDrivers}
                    suggestions={driverS}
                    completeMethod={(e) => {
                        const q = String(e.query || '').toLowerCase();
                        let base = !q ? drivers : drivers.filter(d =>
                            (d.phone_number || '').toLowerCase().includes(q)
                            || `${d.first_name ?? ''} ${d.last_name ?? ''}`.toLowerCase().includes(q)
                        );
                        const selectAll = [{ id: -2, phone_number: 'Wybierz wszystkich', first_name: null, last_name: null } as any];
                        base = [...selectAll, ...base];
                        setDriverS(base);
                    }}
                    selectedItemTemplate={(item) => (<span>{item.phone_number}</span>)}
                    itemTemplate={(item) => (<div>{item.phone_number} {item.first_name || item.last_name ? `• ${(item.first_name ?? '')} ${(item.last_name ?? '')}` : ''}</div>)}
                    field="phone_number"
                    onChange={(e) => {
                        let value = Array.isArray(e.value) ? e.value : [];
                        const hasAll = value.some((v: any) => v?.id === -2);
                        if (hasAll) {
                            value = drivers.slice();
                        }
                        setSelectedDrivers(value);
                    }}
                    placeholder="Wybierz kierowców"
                    onFocus={() => { setDriverS(drivers); driversRef.current?.show?.(); }}
                    onShow={() => { setDriverS([{ id: -2, phone_number: 'Wybierz wszystkich' } as any, ...drivers]); }}
                    onSelect={() => { driversRef.current?.show?.(); }}
                    dropdown
                            />
                        </div>
            <div className="d-flex col-12 justify-content-end gap-2">
                <Button label="Anuluj" className="p-button-outlined p-button-secondary edit-cancel-button" onClick={onCancel} />
                <Button label="Zapisz" className="edit-save-button" disabled={!maker || !model || !plate.trim()} onClick={save} />
                </div>
        </div>
    );
}

function DriverChips({ drivers }: { drivers: FleetDriverListDriver[] }) {
    const containerRef = React.useRef<HTMLDivElement | null>(null);
    const [visibleCount, setVisibleCount] = React.useState<number>(drivers.length);

    React.useEffect(() => {
        setVisibleCount(drivers.length);
    }, [drivers]);

    React.useLayoutEffect(() => {
        const el = containerRef.current;
        if (!el) return;
    
        let frame: number | null = null;
        let resizing = false;
    
        const check = () => {
            if (!el || resizing) return;
            resizing = true;
    
            frame = requestAnimationFrame(() => {
                const fits = el.scrollWidth <= el.clientWidth;
    
                if (!fits && visibleCount > 0) {
                    setVisibleCount((c) => Math.max(0, c - 1));
                }
                else if (fits && visibleCount < drivers.length) {
                    const next = visibleCount + 1;

                    setVisibleCount(next);
                }
    
                resizing = false;
            });
        };
    
        const ro = new ResizeObserver(() => check());
        ro.observe(el);
        check();
    
        return () => {
            ro.disconnect();
            if (frame) cancelAnimationFrame(frame);
        };
    }, [visibleCount, drivers.length]);

    React.useLayoutEffect(() => {
        const el = containerRef.current;
        if (!el) return;
    
        if (el.scrollWidth > el.clientWidth && visibleCount > 0) {
            setVisibleCount((c) => Math.max(0, c - 1));
        }
    }, [visibleCount]);

    return (
        <div className="d-flex gap-2 phone-drivers-line" ref={containerRef}>
            <img alt='driver-configure' style={{ marginRight: '3px', marginTop: '2px' }} src={`/fleet/car/configure-driver.svg`} />
            {drivers.slice(0, visibleCount).map((d) => (
                <span key={d.id} className="vehicle-chip small">{d.phone_number}</span>
            ))}
            {drivers.length > visibleCount && (
                <span className="text-secondary small">+{drivers.length - visibleCount} więcej</span>
            )}
        </div>
    );
}